import * as React from 'react';
import { Checkbox, MuiThemeProvider, Tooltip } from '@material-ui/core';
import { ICellRendererParams } from '@ag-grid-community/core';
import { muiTheme } from 'src/utils/Style/Theme';
import { concat, includes, without, get, isString, first } from 'lodash';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import { disabledStyles, cellStyles, totalCheckboxStyle } from './ValidValuesCheckboxEditor.styles';
import { ClientDataApi } from 'configuration-schemas/confdefnView';
import { getValidValues } from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/StyleEditSection.client';
import { ValidValuesCache, ValidValuesResults } from 'src/services/validValuesCache';
import { isNil } from 'lodash';
import { coalesce } from 'src/utils/Functions/Coalesce';

type Props = ICellRendererParams & {
  isEditable: boolean;
  availableSelections: string[];
  optionsApi?: ClientDataApi;
  group?: boolean;
  validValuesCache: ValidValuesCache;
  comparisonDataIndex?: string;
};

type State = {
  apiOptions?: string[];
};

export default class ValidValuesCheckBoxEditor extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {};
  }

  componentDidMount() {
    this.fetchApiHeaders();
  }

  fetchApiHeaders = async () => {
    const { optionsApi, validValuesCache } = this.props;
    if (!isNil(optionsApi)) {
      const cacheHash = ValidValuesCache.hash({ endpoint: optionsApi.url });
      let results: ValidValuesResults;
      if (validValuesCache.has(cacheHash)) {
        results = validValuesCache.get(cacheHash);
      } else if (validValuesCache.isInflight(cacheHash)) {
        results = await validValuesCache.getInflightPromise(cacheHash);
      } else {
        const resultProm = getValidValues(optionsApi.url, false, false, false);
        validValuesCache.setInflightRequest(cacheHash, resultProm);
        results = await resultProm;
        validValuesCache.set(cacheHash, results);
        validValuesCache.inflightRequestMap.delete(cacheHash);
      }
      this.setState({
        apiOptions: results.map((r: { value: string }) => r.value),
      });
    }
  };

  toggleSelection(sel: string) {
    const currentSelections = this.props.getValue && this.props.getValue();
    // don't allow fully empty selection set
    if (includes(currentSelections, sel)) {
      if (currentSelections.length > 1) {
        this.props.setValue && this.props.setValue(without(currentSelections, sel));
      }
    } else {
      this.props.setValue && this.props.setValue(concat(currentSelections, sel));
    }
  }

  selectionActive(sel: string) {
    if (this.props.getValue) return includes(this.props.getValue(), sel);
    else return;
  }

  render() {
    const disabledClass = this.props.isEditable ? '' : disabledStyles;
    // check if it's grouping, render this
    if (isNil(this.props.value)) {
      return <span />;
    }
    let availableSelections = this.props.availableSelections;
    if (!isNil(this.props.optionsApi)) {
      availableSelections = !isNil(this.state.apiOptions) ? this.state.apiOptions : [];
    }
    let element: JSX.Element[];
    if (this.props.group) {
      element = availableSelections.map((sel) => {
        let groupVal = get(this.props.value, sel, '');
        if (this.props.comparisonDataIndex) {
          // First check if there is a value to compare against at aggregate level
          let compVal = this.props.api.getValue(this.props.comparisonDataIndex, this.props.node);
          if (isNil(compVal)) {
            // If there is nothing at aggregate level, fallback to first child node
            const childN = first(this.props.node.allLeafChildren);
            if (childN) {
              compVal = coalesce(
                this.props.api.getValue(this.props.comparisonDataIndex, childN),
                get(childN.data, this.props.comparisonDataIndex)
              );
            }
          }
          // We check if it's a string to allow clickhouse to return comparison values in the form of:
          // {[colName: string]: unknown}
          if (isString(compVal)) {
            try {
              compVal = JSON.parse(compVal);
              groupVal = `${groupVal} | ${get(compVal, sel, '')}`;
            } catch {
              // This just sets comp to empty as if column was undefined
              groupVal = `${groupVal} | ''`;
            }
          } else if (!isNil(compVal)) {
            // if it's not a string, attempt to get relevant column (falling back to empty string otherwise)
            groupVal = `${groupVal} | ${get(compVal, sel, '')}`;
          }
        }
        return (
          <span key={'' + sel} className={totalCheckboxStyle}>
            {groupVal}
          </span>
        );
      });
    } else {
      element = availableSelections.map((sel, _idx, arr) => {
        const width = `${(1 / arr.length) * 100}%`;
        const disabled = this.selectionActive(sel) && this.props.getValue && this.props.getValue().length <= 1;
        const cursor = disabled || !this.props.isEditable ? 'not-allowed' : 'pointer';
        return (
          <span key={'' + sel} className={`vv-cell-checkbox ${disabledClass}`} style={{ width }}>
            <Tooltip
              title="Must have at least one selection."
              disableFocusListener={!disabled}
              disableHoverListener={!disabled}
            >
              <Checkbox
                checked={this.selectionActive(sel)}
                icon={<RadioButtonUncheckedIcon />}
                checkedIcon={<CheckCircleOutlineIcon />}
                onChange={() => this.toggleSelection(sel)}
                disabled={!this.props.isEditable}
                style={{ cursor }}
                disableRipple={!this.props.isEditable}
              />
            </Tooltip>
          </span>
        );
      });
    }
    return (
      <MuiThemeProvider theme={muiTheme}>
        {/* replaces ag-react-container that was removed, fixes styling */}
        <div style={{ width: '100%' }}>
          <div className={cellStyles}>{element}</div>
        </div>
      </MuiThemeProvider>
    );
  }
}
